'use client'

import { Box, Button, Heading, Spinner, Stack, Text } from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import { isChunkLoadError, reloadOnChunkError } from '@/utils/chunkErrors'

/**
 * App Router error boundary. Rendered within the root layout's providers, so
 * Chakra is available. `reset()` retries the segment.
 *
 * A stale-chunk failure (typical right after a deploy) is recovered by a single
 * hard reload onto the current build rather than shown as a dead-end error —
 * see `@/utils/chunkErrors`.
 */
export default function AppError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  const [reloading, setReloading] = useState(() => isChunkLoadError(error))

  useEffect(() => {
    if (isChunkLoadError(error)) {
      // Returns false if we already retried this chunk: fall back to the error UI.
      setReloading(reloadOnChunkError(error))
      return
    }
    console.error(error)
  }, [error])

  if (reloading) {
    return (
      <Box p={8} display="flex" justifyContent="center">
        <Spinner />
      </Box>
    )
  }

  return (
    <Box p={8}>
      <Stack gap={4} maxW="lg">
        <Heading size="xl">Une erreur est survenue</Heading>
        <Text opacity={0.7}>{error.message}</Text>
        <Button onClick={() => reset()} alignSelf="flex-start">
          Réessayer
        </Button>
      </Stack>
    </Box>
  )
}
